import './style.css';
import { dailyNumber, dailyPair, randomCurated, randomPair } from './daily';
import { Game, INSIGHT_COST, type RaceConfig, type RaceResult } from './game/game';
import type { Action } from './game/input';
import { drawRabbit } from './game/rabbit';
import { F } from './level/types';
import { load, save } from './storage';
import { articleUrl, fetchSummary, searchTitles, setLang, type Summary } from './wiki/api';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const fmtTime = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
};

const data = load();
const params = new URLSearchParams(location.search);
/** Wikipedia edition for custom/explore runs (`?lang=de`). Daily and Random use curated English titles. */
const userLang = /^[a-z-]{2,12}$/.test(params.get('lang') ?? '') ? params.get('lang')! : 'en';
const SCREENS = ['screen-title', 'screen-custom', 'screen-help', 'screen-pause', 'screen-results', 'screen-error'];
let helpReturn: string | null = null;
let lastResult: RaceResult | null = null;

function show(id: string | null): void {
  for (const s of SCREENS) $(s).hidden = s !== id;
  if (id === 'screen-title') startTitleRabbit();
}

function inRace(on: boolean): void {
  $('hud').hidden = !on;
  $('minimap').hidden = !on;
  $('touch').hidden = !on || !touchEnabled();
  if (!on) $('preview').hidden = true;
  if (on) requestAnimationFrame(() => game.resize());
}

function touchEnabled(): boolean {
  const s = data.settings.touchControls;
  return s === 'on' || (s === 'auto' && matchMedia('(pointer: coarse)').matches);
}

// ---------------------------------------------------------------- toasts

function toast(msg: string, kind: 'info' | 'good' | 'bad' = 'info'): void {
  const box = $('toasts');
  const el = document.createElement('div');
  el.className = `toast ${kind}`;
  el.textContent = msg;
  box.appendChild(el);
  while (box.children.length > 3) box.firstElementChild!.remove();
  setTimeout(() => el.classList.add('out'), 3400);
  setTimeout(() => el.remove(), 3900);
}

// ---------------------------------------------------------------- game + hooks

let previewToken = 0;

function renderPreview(s: Partial<Summary> & { title: string }, exit: boolean, hint = true): void {
  const el = $('preview');
  el.classList.toggle('exit', exit);
  const img = s.thumbnail ? `<img src="${esc(s.thumbnail.source)}" alt="" referrerpolicy="no-referrer" />` : '';
  const how =
    game.input.lastDevice === 'gamepad' ? 'Ⓑ' : game.input.lastDevice === 'touch' ? '⤓' : '↓';
  el.innerHTML = `${img}<div>
      <h4>${esc(s.title)}</h4>
      ${s.description ? `<div class="desc">${esc(s.description)}</div>` : ''}
      ${s.extract ? `<p class="ext">${esc(s.extract)}</p>` : ''}
      ${hint ? `<div class="hint">${exit ? `★ Your destination! Press ${how} to dive in` : `Press ${how} to dive in (+1 hop)`}</div>` : ''}
    </div>`;
  el.hidden = false;
}

const game = new Game($<HTMLCanvasElement>('game'), $<HTMLCanvasElement>('minimap'), {
  hud() {
    $('hud-hops').textContent = String(game.hops);
    $('hud-sources').textContent = String(game.sources);
    const ins = $('btn-insight');
    ins.classList.toggle('cant', !game.target || game.sources < INSIGHT_COST);
    ins.classList.toggle('on', game.levelInsight);
  },
  clock(ms) {
    $('hud-time').textContent = fmtTime(ms);
  },
  level(level) {
    document.title = `${level.displayTitle} · Rabbit Hole`;
    const crumbs = $('crumbs');
    crumbs.innerHTML = game.path
      .map((t, i) => `<span class="${i === game.path.length - 1 ? 'cur' : ''}">${esc(t)}</span>`)
      .join('<span class="sep">›</span>');
    if (!data.seenHelp && game.path.length === 1) {
      data.seenHelp = true;
      save(data);
      const touch = touchEnabled();
      setTimeout(
        () =>
          toast(
            touch
              ? 'Run with ◀ ▶, jump with ⤒. Stand on a blue link and tap ⤓ to dive in.'
              : 'Run with ← →, jump with Space. Stand on a blue link and press ↓ to dive in.',
          ),
        900,
      );
      setTimeout(() => toast('Words have physics: bold bounces, italics are icy, and numbers blink.'), 5200);
    }
  },
  standing(title, exit) {
    const token = ++previewToken;
    if (!title) {
      $('preview').hidden = true;
      return;
    }
    renderPreview({ title }, exit);
    fetchSummary(title)
      .then((s) => {
        if (token === previewToken) renderPreview({ ...s, title: s.title || title }, exit);
      })
      .catch(() => undefined);
  },
  toast,
  loading(title) {
    $('loading').hidden = !title;
    if (title) $('loading-title').textContent = title;
  },
  win(r) {
    lastResult = r;
    data.stats.wins++;
    data.stats.hops += r.hops;
    data.stats.sources += r.sources;
    if (r.cfg.mode === 'daily' && r.cfg.daily) {
      const prev = data.daily[r.cfg.daily];
      if (!prev || r.hops < prev.hops || (r.hops === prev.hops && r.timeMs < prev.timeMs))
        data.daily[r.cfg.daily] = { hops: r.hops, timeMs: r.timeMs, path: r.path };
    }
    save(data);
    showResults(r);
  },
  pause() {
    const cur = game.path[game.path.length - 1] ?? '';
    $('pause-route').textContent = game.target
      ? `On “${cur}” after ${game.hops} hop${game.hops === 1 ? '' : 's'}, heading for “${game.target.title}”.`
      : `Exploring “${cur}”, ${game.hops} hop${game.hops === 1 ? '' : 's'} deep.`;
    ($('pause-back') as HTMLButtonElement).disabled = !game.canGoBack;
    show('screen-pause');
  },
  error(msg, retry) {
    $('error-msg').textContent = msg;
    errorRetry = retry;
    inRace(false);
    show('screen-error');
  },
  resume() {
    if (!$('find').hidden) closeFind();
    else if (!$('screen-pause').hidden || (!$('screen-help').hidden && helpReturn === 'screen-pause')) {
      show(null);
      game.resume();
    }
  },
  find: openFind,
});
game.audio.enabled = data.settings.sound;
game.reducedMotion = data.settings.reducedMotion;
game.run();
// Handy for debugging and automated play-testing from the console.
(window as unknown as { rabbitHole: Game }).rabbitHole = game;

let errorRetry: (() => void) | null = null;

function startRace(cfg: RaceConfig): void {
  game.audio.unlock();
  data.stats.races++;
  save(data);
  show(null);
  inRace(true);
  $('hud-mode').textContent =
    cfg.mode === 'daily' ? `Daily #${cfg.daily}` : cfg.mode === 'explore' ? 'Exploring' : 'Race to';
  $('hud-hops').textContent = '0';
  $('hud-time').textContent = '0:00';
  $('hud-time').parentElement!.hidden = cfg.mode === 'explore';
  $('crumbs').innerHTML = '';
  const chip = $('hud-target');
  chip.classList.toggle('explore', !cfg.target);
  if (cfg.target) {
    chip.innerHTML = `<span class="noimg"></span><span class="t">${esc(cfg.target)}</span>`;
    fetchSummary(cfg.target)
      .then((s) => {
        chip.innerHTML = `${s.thumbnail ? `<img src="${esc(s.thumbnail.source)}" alt="" referrerpolicy="no-referrer" />` : '<span class="noimg"></span>'}<span class="t">${esc(s.title)}</span>`;
        chip.title = s.description ? `${s.title}: ${s.description}` : s.title;
      })
      .catch(() => undefined);
  } else {
    chip.innerHTML = '<span class="t">Free explore 🐇</span>';
    chip.title = 'No target: wander wherever the links take you';
  }
  void game.startRace(cfg);
}

$('hud-target').addEventListener('click', () => {
  const t = game.target?.title;
  if (!t) return;
  const token = ++previewToken;
  fetchSummary(t)
    .then((s) => {
      if (token !== previewToken) return;
      renderPreview({ ...s, title: s.title || t }, true, false);
      setTimeout(() => {
        if (token === previewToken) $('preview').hidden = true;
      }, 5000);
    })
    .catch(() => undefined);
});

// ---------------------------------------------------------------- title screen

function setupTitle(): void {
  const n = dailyNumber();
  const pair = dailyPair(n);
  $('daily-num').textContent = `#${n}`;
  const rec = data.daily[n];
  $('daily-pair').textContent = rec
    ? `${pair.start} → ${pair.target} · ✓ ${rec.hops} hops in ${fmtTime(rec.timeMs)}`
    : `${pair.start} → ${pair.target}`;
}

let titleRaf = 0;
function startTitleRabbit(): void {
  cancelAnimationFrame(titleRaf);
  const c = $<HTMLCanvasElement>('title-rabbit');
  const ctx = c.getContext('2d');
  if (!ctx) return;
  const t0 = performance.now();
  const loop = (now: number) => {
    if ($('screen-title').hidden) return;
    const t = (now - t0) / 1000;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    const cycle = t % 3.2;
    const hop = cycle < 0.55 ? Math.sin((cycle / 0.55) * Math.PI) : 0;
    ctx.setTransform(6, 0, 0, 6, 100, 186 - hop * 60);
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(0, hop * 10, 9 - hop * 3, 1.6, 0, 0, Math.PI * 2);
    ctx.fill();
    drawRabbit(ctx, 0, 0, {
      facing: 1,
      vx: 0,
      vy: hop > 0 ? (cycle < 0.27 ? -300 : 300) : 0,
      onGround: hop === 0,
      climbing: false,
      runPhase: 0,
      squash: cycle > 0.55 && cycle < 0.7 ? 0.25 : 0,
      time: t,
      dive: 0,
      flash: false,
      alpha: 1,
    });
    titleRaf = requestAnimationFrame(loop);
  };
  titleRaf = requestAnimationFrame(loop);
}

$('btn-daily').addEventListener('click', () => {
  const n = dailyNumber();
  const p = dailyPair(n);
  startRace({ mode: 'daily', start: p.start, target: p.target, daily: n });
});
$('btn-random').addEventListener('click', () => {
  const p = randomPair();
  startRace({ mode: 'random', start: p.start, target: p.target });
});
$('btn-custom').addEventListener('click', () => openCustom('custom'));
$('btn-explore').addEventListener('click', () => openCustom('explore'));
$('btn-help').addEventListener('click', () => {
  helpReturn = 'screen-title';
  show('screen-help');
});

const optSound = $<HTMLInputElement>('opt-sound');
const optMotion = $<HTMLInputElement>('opt-motion');
optSound.checked = data.settings.sound;
optMotion.checked = data.settings.reducedMotion;
optSound.addEventListener('change', () => {
  data.settings.sound = optSound.checked;
  game.audio.enabled = optSound.checked;
  save(data);
  if (optSound.checked) {
    game.audio.unlock();
    game.audio.play('coin');
  }
});
optMotion.addEventListener('change', () => {
  data.settings.reducedMotion = optMotion.checked;
  game.reducedMotion = optMotion.checked;
  save(data);
});

document.querySelectorAll<HTMLElement>('[data-close]').forEach((b) =>
  b.addEventListener('click', () => {
    const screen = b.closest('.screen')?.id;
    if (screen === 'screen-help' && helpReturn) show(helpReturn);
    else show('screen-title');
  }),
);

// ---------------------------------------------------------------- custom race + autocomplete

let customMode: 'custom' | 'explore' = 'custom';

function openCustom(mode: 'custom' | 'explore'): void {
  customMode = mode;
  setLang(userLang);
  const edition = userLang === 'en' ? '' : ` · ${userLang}.wikipedia.org`;
  $('custom-heading').textContent = (mode === 'explore' ? 'Free explore' : 'Custom race') + edition;
  $('custom-to-field').hidden = mode === 'explore';
  $('custom-share').hidden = mode === 'explore';
  $('custom-error').hidden = true;
  show('screen-custom');
  setTimeout(() => $<HTMLInputElement>('custom-from').focus(), 50);
}

function autocomplete(input: HTMLInputElement): void {
  const list = input.parentElement!.querySelector('.ac-list') as HTMLUListElement;
  let items: string[] = [];
  let sel = -1;
  let ctrl: AbortController | null = null;
  let timer = 0;
  const render = () => {
    list.innerHTML = items
      .map((t, i) => `<li role="option" data-i="${i}" aria-selected="${i === sel}">${esc(t)}</li>`)
      .join('');
    list.hidden = !items.length;
  };
  const choose = (i: number) => {
    if (!items[i]) return;
    input.value = items[i];
    items = [];
    render();
  };
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = window.setTimeout(async () => {
      ctrl?.abort();
      ctrl = new AbortController();
      try {
        items = await searchTitles(input.value, ctrl.signal);
        sel = -1;
        render();
      } catch {
        /* aborted or offline */
      }
    }, 150);
  });
  input.addEventListener('keydown', (e) => {
    if (list.hidden) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      sel = (sel + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      render();
    } else if (e.key === 'Enter' && sel >= 0) {
      e.preventDefault();
      choose(sel);
    } else if (e.key === 'Escape') {
      items = [];
      render();
    }
  });
  list.addEventListener('mousedown', (e) => {
    const li = (e.target as HTMLElement).closest('li');
    if (li) {
      e.preventDefault();
      choose(Number(li.dataset.i));
    }
  });
  input.addEventListener('blur', () =>
    setTimeout(() => {
      items = [];
      render();
    }, 120),
  );
}

autocomplete($<HTMLInputElement>('custom-from'));
autocomplete($<HTMLInputElement>('custom-to'));

document.querySelectorAll<HTMLButtonElement>('[data-dice]').forEach((b) =>
  b.addEventListener('click', () => {
    const kind = b.dataset.dice as 'start' | 'target';
    $<HTMLInputElement>(kind === 'start' ? 'custom-from' : 'custom-to').value = randomCurated(kind);
  }),
);

function challengeUrl(from: string, to?: string, lang = userLang): string {
  const u = new URL(location.href);
  u.search = '';
  u.hash = '';
  if (lang !== 'en') u.searchParams.set('lang', lang);
  u.searchParams.set('from', from);
  if (to) u.searchParams.set('to', to);
  return u.toString();
}

$('custom-share').addEventListener('click', async () => {
  const from = $<HTMLInputElement>('custom-from').value.trim();
  const to = $<HTMLInputElement>('custom-to').value.trim();
  if (!from || !to) {
    showCustomError('Fill in both articles first.');
    return;
  }
  await copy(challengeUrl(from, to));
  toast('Challenge link copied! Send it to a friend.', 'good');
});

function showCustomError(msg: string): void {
  const e = $('custom-error');
  e.textContent = msg;
  e.hidden = false;
}

$('custom-form').addEventListener('submit', (e) => {
  e.preventDefault();
  const from = $<HTMLInputElement>('custom-from').value.trim();
  const to = $<HTMLInputElement>('custom-to').value.trim();
  if (!from) return showCustomError('Where should the rabbit start?');
  if (customMode === 'explore') return startRace({ mode: 'explore', start: from, lang: userLang });
  if (!to) return showCustomError('Where are you headed? Pick a target (or use Free Explore).');
  if (from.toLowerCase() === to.toLowerCase()) return showCustomError('Start and target must be different articles.');
  startRace({ mode: 'custom', start: from, target: to, lang: userLang });
});

// ---------------------------------------------------------------- HUD buttons, pause, find

$('btn-pause').addEventListener('click', () => game.pause());
$('btn-insight').addEventListener('click', () => void game.useInsight());
$('btn-find').addEventListener('click', () => {
  if (game.state === 'play') openFind();
});

$('pause-resume').addEventListener('click', () => {
  show(null);
  game.resume();
});
$('pause-back').addEventListener('click', () => {
  show(null);
  game.goBack();
});
$('pause-restart').addEventListener('click', () => {
  if (game.cfg) startRace(game.cfg);
});
$('pause-help').addEventListener('click', () => {
  helpReturn = 'screen-pause';
  show('screen-help');
});
$('pause-wiki').addEventListener('click', () => {
  const cur = game.path[game.path.length - 1];
  if (cur) window.open(articleUrl(cur), '_blank', 'noopener');
});
$('pause-quit').addEventListener('click', quitToMenu);

function quitToMenu(): void {
  game.quit();
  const keep = userLang === 'en' ? '' : `?lang=${encodeURIComponent(userLang)}`;
  if (location.search !== keep) history.replaceState(null, '', location.pathname + keep);
  inRace(false);
  document.title = 'Rabbit Hole: a Wikipedia platformer';
  setupTitle();
  show('screen-title');
}

window.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (!$('screen-pause').hidden) {
    show(null);
    game.resume();
  } else if (!$('screen-help').hidden) show(helpReturn ?? 'screen-title');
  else if (!$('screen-custom').hidden) show('screen-title');
});

let findSel = 0;
let findItems: ReturnType<typeof game.findMatches> = [];

function openFind(): void {
  game.hold();
  $('toasts').replaceChildren();
  const box = $('find');
  box.hidden = false;
  const input = $<HTMLInputElement>('find-input');
  input.value = '';
  findItems = [];
  renderFind();
  input.focus();
}

function closeFind(resume = true): void {
  $('find').hidden = true;
  $<HTMLInputElement>('find-input').blur();
  if (resume) game.resume();
}

function renderFind(): void {
  const ul = $('find-results');
  const q = $<HTMLInputElement>('find-input').value.trim();
  if (!q) {
    ul.innerHTML = `<li class="muted">Type part of a link, e.g. “${esc(game.target?.title.split(' ')[0] ?? 'history')}”</li>`;
    return;
  }
  if (!findItems.length) {
    ul.innerHTML = '<li class="muted">No links match on this page.</li>';
    return;
  }
  ul.innerHTML = findItems
    .map((o, i) => {
      const st = game.linkState(o.title);
      const cls = st & F.EXIT ? 'exit' : st & F.HOT ? 'hot' : '';
      const label = o.text === o.title ? o.title : `${o.text} → ${o.title}`;
      return `<li data-i="${i}" class="${cls}" aria-selected="${i === findSel}"><span class="t">${esc(label)}</span><span class="s">${esc(game.sectionTitle(o.section))}</span></li>`;
    })
    .join('');
}

$('find-input').addEventListener('input', () => {
  findItems = game.findMatches($<HTMLInputElement>('find-input').value);
  findSel = 0;
  renderFind();
});
$('find-input').addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    closeFind();
  } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    if (findItems.length) findSel = (findSel + (e.key === 'ArrowDown' ? 1 : -1) + findItems.length) % findItems.length;
    renderFind();
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const o = findItems[findSel];
    closeFind(false);
    if (o) game.focusOcc(o.id);
    else game.resume();
  }
});
$('find-results').addEventListener('mousedown', (e) => {
  const li = (e.target as HTMLElement).closest('li[data-i]') as HTMLElement | null;
  if (!li) return;
  e.preventDefault();
  const o = findItems[Number(li.dataset.i)];
  closeFind(false);
  if (o) game.focusOcc(o.id);
  else game.resume();
});
$('find-input').addEventListener('blur', () => {
  setTimeout(() => {
    if (!$('find').hidden) closeFind();
  }, 150);
});

// ---------------------------------------------------------------- results + sharing

function stars(hops: number): number {
  return hops <= 3 ? 3 : hops <= 5 ? 2 : 1;
}

function showResults(r: RaceResult): void {
  $('toasts').replaceChildren();
  $('res-kicker').textContent =
    r.cfg.mode === 'daily' ? `Daily Burrow #${r.cfg.daily}` : r.cfg.mode === 'custom' ? 'Custom race' : 'Random race';
  $('res-title').textContent = `You reached “${r.target}”!`;
  const s = stars(r.hops);
  $('res-stars').innerHTML = '★'.repeat(s) + `<span class="off">${'★'.repeat(3 - s)}</span>`;
  $('res-stats').innerHTML = [
    [String(r.hops), r.hops === 1 ? 'hop' : 'hops'],
    [fmtTime(r.timeMs), 'time'],
    [String(r.sources), 'sources'],
    [String(r.deaths), r.deaths === 1 ? 'tumble' : 'tumbles'],
  ]
    .map(([v, l]) => `<div><b>${esc(v)}</b><span>${esc(l)}</span></div>`)
    .join('');
  $('res-path').innerHTML = r.path
    .map((t) => `<li><a href="${esc(articleUrl(t))}" target="_blank" rel="noopener">${esc(t)}</a></li>`)
    .join('');
  const sc = $('res-shortcut');
  if (r.shortcut && r.shortcut.length - 1 < r.hops) {
    sc.hidden = false;
    sc.textContent = `Shortcut spotted: ${r.shortcut.join(' → ')} (${r.shortcut.length - 1} hop${r.shortcut.length === 2 ? '' : 's'}).`;
  } else sc.hidden = true;
  show('screen-results');
}

function shareText(r: RaceResult): string {
  const title =
    r.cfg.mode === 'daily' ? `🐇 Rabbit Hole · Daily #${r.cfg.daily}` : '🐇 Rabbit Hole';
  const holes = '🕳️'.repeat(Math.max(0, r.hops - 1)) + '🎯';
  const url =
    r.cfg.mode === 'daily' ? location.origin + location.pathname : challengeUrl(r.path[0], r.target, r.cfg.lang ?? 'en');
  return `${title}\n${r.path[0]} → ${r.target}\n${holes} ${r.hops} hop${r.hops === 1 ? '' : 's'} · ⏱ ${fmtTime(r.timeMs)} · ${'★'.repeat(stars(r.hops))}\n${url}`;
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
}

$('res-share').addEventListener('click', async () => {
  if (!lastResult) return;
  const text = shareText(lastResult);
  if (navigator.share && matchMedia('(pointer: coarse)').matches) {
    try {
      await navigator.share({ text });
      return;
    } catch {
      /* fall back to clipboard */
    }
  }
  toast((await copy(text)) ? 'Result copied to clipboard!' : 'Could not copy. Your browser blocked it.', 'good');
});
$('res-again').addEventListener('click', () => {
  if (!lastResult) return;
  if (lastResult.cfg.mode === 'random') {
    const p = randomPair();
    startRace({ mode: 'random', start: p.start, target: p.target });
  } else startRace(lastResult.cfg);
});
$('res-menu').addEventListener('click', quitToMenu);
$('error-retry').addEventListener('click', () => {
  show(null);
  inRace(true);
  errorRetry?.();
});
$('error-menu').addEventListener('click', quitToMenu);

// ---------------------------------------------------------------- touch controls

document.querySelectorAll<HTMLButtonElement>('#touch button').forEach((b) => {
  const act = b.dataset.act as Action;
  const on = (e: PointerEvent) => {
    e.preventDefault();
    b.setPointerCapture(e.pointerId);
    b.classList.add('on');
    game.input.setTouch(act, true);
  };
  const off = () => {
    b.classList.remove('on');
    game.input.setTouch(act, false);
  };
  b.addEventListener('pointerdown', on);
  b.addEventListener('pointerup', off);
  b.addEventListener('pointercancel', off);
  b.addEventListener('lostpointercapture', off);
  b.addEventListener('contextmenu', (e) => e.preventDefault());
});
if (touchEnabled()) document.body.classList.add('touch');

// Browsers only allow audio after a user gesture.
const unlock = () => {
  game.audio.unlock();
  window.removeEventListener('pointerdown', unlock);
  window.removeEventListener('keydown', unlock);
};
window.addEventListener('pointerdown', unlock);
window.addEventListener('keydown', unlock);

// ---------------------------------------------------------------- boot

setupTitle();
setLang(userLang);
const from = params.get('from');
const to = params.get('to');
const explore = params.get('explore');
if (from && to) startRace({ mode: 'custom', start: from, target: to, lang: userLang });
else if (explore) startRace({ mode: 'explore', start: explore, lang: userLang });
else if (params.has('daily')) $('btn-daily').click();
else show('screen-title');
